
export interface TierLimits {
  customers: number;
  karigars: number;
  orders: number;
  storage: string; // e.g., "1GB", "unlimited"
  features: string[];
}

export interface PricingTier {
  id: string;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  limits: TierLimits;
  features: string[];
  popular?: boolean;
}

export const TIERS: Record<string, PricingTier> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    billingCycle: 'monthly',
    limits: {
      customers: 50,
      karigars: 0,
      orders: 10,
      storage: '1GB',
      features: ['Basic profile', 'Order management', 'Customer communication', 'Basic analytics']
    },
    features: [
      'Up to 50 customers',
      'Up to 10 orders per month',
      'Basic profile management',
      'Order management system',
      'Customer communication',
      'Basic analytics dashboard',
      'Email support'
    ]
  },
  basic: {
    id: 'basic',
    name: 'Basic',
    price: 999, // Monthly price; yearly: ₹9,999 (17% discount)
    billingCycle: 'monthly',
    limits: {
      customers: 500,
      karigars: 0,
      orders: 100,
      storage: '10GB',
      features: ['Advanced analytics', 'Custom branding', 'Priority support']
    },
    features: [
      'Up to 500 customers',
      'Up to 100 orders per month',
      'Advanced analytics dashboard',
      'Custom branding options',
      'Priority customer support',
      'Enhanced order management',
      'Customer insights & reports'
    ]
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    price: 1999, // Monthly price; yearly: ₹19,999 (17% discount)
    billingCycle: 'monthly',
    limits: {
      customers: 5000,
      karigars: 0,
      orders: 1000,
      storage: '100GB',
      features: ['API access', 'Custom integrations', 'Advanced reporting']
    },
    features: [
      'Up to 5,000 customers',
      'Up to 1,000 orders per month',
      'API access for integrations',
      'Custom integrations',
      'Advanced reporting & analytics',
      'Multi-location support',
      'Inventory management',
      'Advanced customer segmentation'
    ],
    popular: true
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: 2999, // Monthly price; yearly: ₹29,999 (17% discount)
    billingCycle: 'monthly',
    limits: {
      customers: -1, // unlimited
      karigars: 0,
      orders: -1, // unlimited
      storage: '1000GB',
      features: ['White-label', 'Dedicated support', 'Custom features']
    },
    features: [
      'Unlimited customers',
      'Unlimited orders',
      'White-label solution',
      'API access for integrations',
      'Advanced reporting & analytics',
      'Multi-location support',
      'Inventory management'
    ]
  }
};

export const getTierLimits = (tierId: string): TierLimits => {
  return TIERS[tierId]?.limits || TIERS.free.limits;
};

export const canAddCustomer = (currentCount: number, tierId: string): boolean => {
  const limits = getTierLimits(tierId);
  return limits.customers === -1 || currentCount < limits.customers;
};

export const canAddKarigar = (currentCount: number, tierId: string): boolean => {
  const limits = getTierLimits(tierId);
  return limits.karigars === -1 || currentCount < limits.karigars;
};

export const canAddOrder = (currentCount: number, tierId: string): boolean => {
  const limits = getTierLimits(tierId);
  return limits.orders === -1 || currentCount < limits.orders;
};

export const getUpgradeMessage = (tierId: string, type: 'customer' | 'karigar' | 'order'): string => {
  const currentTier = TIERS[tierId];
  const limits = getTierLimits(tierId);
  
  switch (type) {
    case 'customer':
      return `You've reached the limit of ${limits.customers} customers for your ${currentTier.name} plan. Upgrade to add more customers.`;
    case 'karigar':
      return `You've reached the limit of ${limits.karigars} karigars for your ${currentTier.name} plan. Upgrade to add more karigars.`;
    case 'order':
      return `You've reached the limit of ${limits.orders} orders for your ${currentTier.name} plan. Upgrade to add more orders.`;
    default:
      return 'You\'ve reached your plan limit. Upgrade to continue.';
  }
};
